import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import usePwaManifest from '../utils/usePwaManifest';

export default function Login() {
  usePwaManifest('/manifest.json');
  const [step, setStep] = useState(1); // 1 = ID+phone, 2 = code
  const [form, setForm] = useState({ workerIdNumber: '', phoneNumber: '' });
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const codeRefs = useRef([]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  async function handleRequestCode(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/request-code', form);
      setMaskedEmail(res.data.email);
      setStep(2);
      setCountdown(60);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשליחת הקוד');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyCode(fullCode) {
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/verify-code', {
        workerIdNumber: form.workerIdNumber,
        code: fullCode,
      });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('workerFirstName', res.data.workerFirstName);
      localStorage.setItem('workerLastName', res.data.workerLastName);
      localStorage.setItem('expiresAt', res.data.expiresAt);
      // A fresh login always lands on project selection — it used to jump
      // back to whatever admin sub-page the session happened to expire on
      // (via redirectAfterLogin, set by PrivateRoute), which is confusing
      // context to land in cold instead of picking a project first.
      localStorage.removeItem('redirectAfterLogin');
      navigate('/projects');
    } catch (err) {
      setError(err.response?.data?.error || 'קוד לא נכון');
      setCode(['', '', '', '', '', '']);
      codeRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }

  function handleCodeChange(index, value) {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    const fullCode = newCode.join('');
    if (fullCode.length === 6 && newCode.every(d => d !== '')) {
      handleVerifyCode(fullCode);
    }
  }

  function handleCodeKeyDown(index, e) {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  }

  function handleCodePaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split('');
      setCode(newCode);
      codeRefs.current[5]?.focus();
      handleVerifyCode(pasted);
    }
  }

  async function handleResend() {
    if (countdown > 0) return;
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/request-code', form);
      setMaskedEmail(res.data.email);
      setCountdown(60);
      setCode(['', '', '', '', '', '']);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשליחת הקוד');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f2d55 0%, #1a4a8a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="login-container">
        <img src="/logos/mabat_white_600.png" alt="מבט" width={300} height={164} style={{ display: 'block', margin: '0 auto 16px', width: 300, height: 'auto', aspectRatio: '600 / 327', objectFit: 'contain' }} />
        <p className="login-subtitle">מערכת ניהול קהילה — כניסה לעובדים</p>
        {error && <div className="error-msg">{error}</div>}

        {step === 1 && (
          <form onSubmit={handleRequestCode}>
            <div className="form-group">
              <label>תעודת זהות</label>
              <input
                type="text"
                inputMode="numeric"
                value={form.workerIdNumber}
                onChange={e => setForm({ ...form, workerIdNumber: e.target.value })}
                placeholder="הזן מספר תעודת זהות"
                required
              />
            </div>
            <div className="form-group">
              <label>מספר טלפון</label>
              <input
                type="tel"
                inputMode="tel"
                value={form.phoneNumber}
                onChange={e => setForm({ ...form, phoneNumber: e.target.value })}
                placeholder="הזן מספר טלפון"
                required
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', marginTop: 8, padding: '11px', fontSize: '1rem' }}
              disabled={loading}
            >
              {loading ? 'שולח קוד...' : 'שלח קוד כניסה'}
            </button>
          </form>
        )}

        {step === 2 && (
          <div>
            <p style={{ textAlign: 'center', color: '#374151', marginBottom: 6, fontSize: '0.95rem' }}>
              קוד אימות נשלח לאימייל
            </p>
            <p style={{ textAlign: 'center', color: '#1a4a8a', fontWeight: 600, marginBottom: 24, fontSize: '0.95rem', direction: 'ltr' }}>
              {maskedEmail}
            </p>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 24, direction: 'ltr' }}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={el => codeRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleCodeChange(i, e.target.value)}
                  onKeyDown={e => handleCodeKeyDown(i, e)}
                  onPaste={i === 0 ? handleCodePaste : undefined}
                  style={{
                    width: 48,
                    height: 56,
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    border: '2px solid #d1d5db',
                    borderRadius: 10,
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    fontFamily: 'monospace',
                  }}
                  onFocus={e => e.target.style.borderColor = '#1a4a8a'}
                  onBlur={e => e.target.style.borderColor = '#d1d5db'}
                  disabled={loading}
                />
              ))}
            </div>

            {loading && (
              <p style={{ textAlign: 'center', color: '#1a4a8a', fontSize: '0.9rem', marginBottom: 16 }}>
                מאמת...
              </p>
            )}

            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <button
                type="button"
                onClick={handleResend}
                disabled={countdown > 0 || loading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: countdown > 0 ? '#9ca3af' : '#1a4a8a',
                  cursor: countdown > 0 ? 'default' : 'pointer',
                  fontSize: '0.9rem',
                  fontFamily: 'Heebo, Arial, sans-serif',
                  textDecoration: countdown > 0 ? 'none' : 'underline',
                }}
              >
                {countdown > 0 ? `שליחה חוזרת בעוד ${countdown} שניות` : 'שלח קוד חדש'}
              </button>
            </div>

            <button
              type="button"
              onClick={() => { setStep(1); setCode(['', '', '', '', '', '']); setError(''); }}
              className="btn btn-secondary"
              style={{ width: '100%', padding: '10px', fontSize: '0.9rem' }}
            >
              חזרה
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
